import type { Identifier, RaRecord } from "ra-core";

/**
 * Almikia Epic EL2 (TASK-073): BPO carousel resources. Column names are identical to
 * `public.project_office_hours`, `project_guardias`, `advisor_unavailability`,
 * `lead_assigned_events`, `lead_carousel_notices` and the `project_guardias_roster` view
 * (TASK-070). Re-exported from `types.ts`. Vocabulary: "guardia" and "disponibilidad"; nothing
 * here tracks attendance, and no shape carries a ranking or a per-advisor comparison.
 * Times are "HH:MM" or "HH:MM:SS" and dates "YYYY-MM-DD", all in America/Mexico_City.
 */

/** Why a lead went to an advisor (`lead_assignments.reason`, NULL for rows logged before EL2). */
export const LEAD_ASSIGNMENT_REASONS = [
  "lead_nuevo",
  "proteccion_90_dias",
  "fallback_omision",
  "aviso_10_min",
  "reasignacion_24h",
  "manual",
  // Almikia Epic EL3 (owner 2026-10-05): documented reassignments (Direccion on a protected lead,
  // the departure handover, the Team Leader from a notice) and the ping-pong cap.
  "reasignacion_direccion",
  "baja_de_asesora",
  "tope_reasignaciones",
  "reasignacion_lider",
] as const;
export type LeadAssignmentReason = (typeof LEAD_ASSIGNMENT_REASONS)[number];

/**
 * Almikia Epic EL3: lead TYPES (`deals.lead_type`, who generated the lead) that are NEVER
 * reassigned by the system or by normal roles. Not to be confused with the lead ORIGIN
 * (`lead_sources`).
 */
export const PROTECTED_LEAD_TYPES = ["proactivo", "colega"] as const;

/** True for a proactivo / colega lead (null / unknown types are not protected). */
export const isProtectedLeadType = (leadType: string | null | undefined) =>
  leadType != null &&
  (PROTECTED_LEAD_TYPES as readonly string[]).includes(leadType);

/**
 * True when the identity holds the per-person permission `sales.can_reassign_protected_leads`
 * (set by direccion only; NOT a role). Tolerates a missing identity or a missing flag.
 */
export const canReassignProtectedLeads = (
  identity: Record<string, unknown> | null | undefined,
) => identity?.can_reassign_protected_leads === true;

/** Display data only: the office hours a project shows. A closed day has no row. */
export type ProjectOfficeHours = {
  project_id: Identifier;
  /** ISO weekday, 1 = Monday .. 7 = Sunday. */
  weekday: number;
  opens_at: string;
  closes_at: string;
} & Pick<RaRecord, "id">;

/** A Monday-Friday window in which an advisor receives the project's new leads. */
export type ProjectGuardia = {
  project_id: Identifier;
  sales_id: Identifier;
  guardia_date: string;
  start_time: string;
  end_time: string;
  created_by?: Identifier | null;
  created_at: string;
  /** A guardia ends via `ended_at`, never DELETE. */
  ended_at?: string | null;
} & Pick<RaRecord, "id">;

/** "No disponible" with a start and an end (advisor-level). Cancelled via `cancelled_at`. */
export type AdvisorUnavailability = {
  sales_id: Identifier;
  starts_at: string;
  ends_at: string;
  created_by?: Identifier | null;
  created_at: string;
  cancelled_at?: string | null;
} & Pick<RaRecord, "id">;

/** One row of the `project_guardias_roster` view: a non-ended guardia with the advisor's name. */
export type ProjectGuardiaRosterRow = {
  project_id: Identifier;
  sales_id: Identifier;
  first_name: string;
  last_name: string;
  guardia_date: string;
  start_time: string;
  end_time: string;
} & Pick<RaRecord, "id">;

/**
 * "Who is on guardia now", derived from the roster and the office hours only (no presence or
 * attendance data exists). Names only.
 */
export type OnGuardiaNowEntry = {
  sales_id: Identifier;
  first_name: string;
  last_name: string;
};

/** The skip-rule parameters of a project (columns of `lead_assignment_rules`); null = rule off. */
export type CarouselSettings = {
  project_id: Identifier;
  max_open_unattended_leads: number | null;
  overdue_threshold_hours: number | null;
};

/**
 * The project-level 'Activa' flag: `lead_assignment_eligible_advisors.is_eligible`
 * (no row = not active).
 */
export type ProjectAdvisorFlag = {
  project_id: Identifier;
  sales_id: Identifier;
  is_eligible: boolean;
} & Pick<RaRecord, "id">;

/** Outbox row for the EW2 first message ("lead assigned"); stores no phone or email. */
export type LeadAssignedEvent = {
  deal_id: Identifier;
  assignment_id?: Identifier | null;
  project_id?: Identifier | null;
  sales_id?: Identifier | null;
  advisor_first_name?: string | null;
  reason?: LeadAssignmentReason | null;
  created_at: string;
  processed_at?: string | null;
} & Pick<RaRecord, "id">;

export const CAROUSEL_NOTICE_KINDS = [
  "aviso_10_min",
  "reasignacion_24h",
  "sin_asesora_disponible",
  // Almikia Epic EL3: ping-pong cap (the lead went to the Team Leader), the 2-day cadence alert
  // and the sixth-touch "Cerrado perdido" suggestion.
  "reasignacion_tope",
  "alerta_cadencia_2d",
  "sugerencia_cerrado_perdido",
] as const;
export type CarouselNoticeKind = (typeof CAROUSEL_NOTICE_KINDS)[number];

/** The only reason text of the "Cerrado perdido" suggestion (no loss-reason catalogue exists). */
export const CERRADO_PERDIDO_SUGGESTION_REASON = "No contestó";

/** Idempotency + audit of the carousel notices: one row per (deal, assignment, kind). */
export type LeadCarouselNotice = {
  deal_id: Identifier;
  assignment_id?: Identifier | null;
  project_id?: Identifier | null;
  sales_id?: Identifier | null;
  kind: CarouselNoticeKind;
  /** EL3: the lead is proactivo / colega, so the notice must not promise a reassignment. */
  is_protected_lead?: boolean;
  /** EL3: start of the touch-gap an `alerta_cadencia_2d` notice reports (null for other kinds). */
  touch_gap_since?: string | null;
  created_at: string;
} & Pick<RaRecord, "id">;

/** Outbox row of a cadence notice for the internal WhatsApp notices module (EN); no phone, no email. */
export type LeadCadenceEvent = {
  notice_id: Identifier;
  deal_id: Identifier;
  project_id?: Identifier | null;
  sales_id?: Identifier | null;
  kind: Extract<
    CarouselNoticeKind,
    "alerta_cadencia_2d" | "sugerencia_cerrado_perdido"
  >;
  created_at: string;
  processed_at?: string | null;
} & Pick<RaRecord, "id">;

/** Arguments of `reassignProtectedLead` (the Direccion reassignment of a proactivo / colega lead). */
export type ReassignProtectedLeadParams = {
  dealId: Identifier;
  newSalesId: Identifier;
  /** Mandatory written reason. */
  reason: string;
};

/** Arguments of `reassignLeadFromNotice` (the Team Leader reassigns from a notice). */
export type ReassignLeadFromNoticeParams = {
  noticeId: Identifier;
  newSalesId: Identifier;
  /** Mandatory written reason. */
  reason: string;
};

/** Arguments of `setCanReassignProtectedLeads` (the Direccion-only permission switch). */
export type SetCanReassignProtectedLeadsParams = {
  salesId: Identifier;
  value: boolean;
};

/** What both reassignment methods return (same shape as the RPCs). */
export type ReassignLeadResult = {
  outcome: "assigned";
  sales_id: Identifier;
  previous_sales_id: Identifier | null;
  assignment_id: Identifier | null;
};
